import { multisetCharsForScramble } from "./scrambleTextStyle";

/**
 * .no-visual 섹션 내 [data-cursor-text] — 커스텀 커서 + ScrambleText
 */
export class HomeVisualCursor {
    static init(): void {
        const gsap = window.gsap;
        if (!gsap) return;
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches)
            return;

        const section = document.querySelector(".no-visual");
        if (!section) return;

        let cursor = document.querySelector(".visual-follow-cursor");
        if (!cursor) {
            cursor = document.createElement("div");
            cursor.className = "visual-follow-cursor";
            cursor.innerHTML = `<div class="visual-follow-cursor-text"></div>`;
            document.body.appendChild(cursor);
        }

        const text = cursor.querySelector(".visual-follow-cursor-text");
        if (!text) return;

        const STP = (
            window as unknown as { ScrambleTextPlugin?: object }
        ).ScrambleTextPlugin;
        if (typeof STP !== "undefined") {
            gsap.registerPlugin(STP);
        }
        const hasScramble = typeof STP !== "undefined";

        let currentTarget: Element | null = null;
        let isVisible = false;
        let hideTimer: ReturnType<typeof setTimeout> | null = null;
        let brakeTween: { kill: () => void } | null = null;
        let lastCursorPhrase = "";

        let mouseX = window.innerWidth * 0.5;
        let mouseY = window.innerHeight * 0.5;
        let renderX = mouseX;
        let renderY = mouseY;
        let prevMouseX = mouseX;
        let prevMouseY = mouseY;
        let lastDX = 0;
        let lastDY = 0;

        const offsetX = -12;
        const offsetY = 10;
        const followEase = 0.06;

        gsap.set(cursor, {
            xPercent: -35,
            yPercent: -110,
            x: renderX + offsetX,
            y: renderY + offsetY,
            autoAlpha: 0,
        });

        function scrambleIn(value: string): void {
            const v = value.replace(/\s+/g, " ").trim() || value;
            lastCursorPhrase = v;
            if (!hasScramble) {
                text.textContent = v;
                return;
            }
            gsap.killTweensOf(text);
            text.textContent = "";
            const chars = multisetCharsForScramble(v);
            gsap.to(text, {
                duration: 1.12,
                ease: "power2.out",
                scrambleText: {
                    text: v,
                    chars,
                    speed: 0.38,
                    revealDelay: 0.05,
                },
                overwrite: true,
            });
        }

        function scrambleOut(): number {
            if (!hasScramble) {
                text.textContent = "";
                return 720;
            }
            gsap.killTweensOf(text);
            const chars = multisetCharsForScramble(lastCursorPhrase);
            const dur = 0.78;
            gsap.to(text, {
                duration: dur,
                ease: "power2.in",
                scrambleText: {
                    text: "",
                    chars,
                    speed: 0.36,
                },
                overwrite: true,
            });
            return Math.round(dur * 1000) + 40;
        }

        function showCursor(target: Element): void {
            if (hideTimer) clearTimeout(hideTimer);
            if (brakeTween) brakeTween.kill();

            currentTarget = target;
            isVisible = true;
            gsap.set(cursor, { autoAlpha: 1 });
            const t = (target as HTMLElement).dataset.cursorText || "VIEW VIDEO";
            scrambleIn(t);
        }

        function hideCursor(): void {
            if (!isVisible) return;
            isVisible = false;
            currentTarget = null;
            if (brakeTween) brakeTween.kill();

            const brakeX = gsap.utils.clamp(-22, 22, lastDX * 0.55);
            const brakeY = gsap.utils.clamp(-16, 16, lastDY * 0.55);
            const hideDelay = scrambleOut();

            brakeTween = gsap.to(cursor, {
                x: renderX + offsetX + brakeX,
                y: renderY + offsetY + brakeY,
                duration: 0.65,
                ease: "power4.out",
                overwrite: "auto",
            });

            if (hideTimer) clearTimeout(hideTimer);
            hideTimer = setTimeout(() => {
                gsap.set(cursor, { autoAlpha: 0 });
                text.textContent = "";
            }, hideDelay);
        }

        function updateHoverTarget(clientX: number, clientY: number): void {
            const hovered = document.elementFromPoint(clientX, clientY);
            const nextTarget = hovered
                ? hovered.closest("[data-cursor-text]")
                : null;

            if (!nextTarget || !section.contains(nextTarget)) {
                if (isVisible) hideCursor();
                return;
            }
            if (currentTarget !== nextTarget || !isVisible) {
                showCursor(nextTarget);
            }
        }

        function onMove(e: MouseEvent): void {
            mouseX = e.clientX;
            mouseY = e.clientY;
            lastDX = mouseX - prevMouseX;
            lastDY = mouseY - prevMouseY;
            prevMouseX = mouseX;
            prevMouseY = mouseY;
            updateHoverTarget(mouseX, mouseY);
        }

        function render(): void {
            renderX += (mouseX - renderX) * followEase;
            renderY += (mouseY - renderY) * followEase;
            if (isVisible) {
                gsap.set(cursor, {
                    x: renderX + offsetX,
                    y: renderY + offsetY,
                });
            }
            requestAnimationFrame(render);
        }

        window.addEventListener("mousemove", onMove);
        section.addEventListener("mouseleave", () => hideCursor());
        window.addEventListener("blur", () => hideCursor());
        render();
    }
}
